import { useEffect, useRef, useState } from 'react'
import { logger } from '@/lib/logger';

interface PerformanceMetrics {
  renderTime: number
  memoryUsage?: number
  renderCount: number
  lastRenderTime: number
}

interface UsePerformanceMonitorOptions {
  componentName?: string
  logThreshold?: number // Log if render time exceeds this (ms)
  trackMemory?: boolean
}

export function usePerformanceMonitor(options: UsePerformanceMonitorOptions = {}) {
  const { componentName = 'Unknown', logThreshold = 16, trackMemory = false } = options

  const renderStartTime = useRef<number>(0)
  const metricsRef = useRef<PerformanceMetrics>({
    renderTime: 0,
    renderCount: 0,
    lastRenderTime: Date.now()
  })
  const [metrics, setMetrics] = useState<PerformanceMetrics>(metricsRef.current)

  // Update metrics state periodically instead of on every render
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics({ ...metricsRef.current })
    }, 1000) // Update every second

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    renderStartTime.current = performance.now()

    return () => {
      const renderEndTime = performance.now()
      const renderTime = renderEndTime - renderStartTime.current

      // Update ref instead of state to avoid infinite loop
      metricsRef.current = {
        renderTime,
        renderCount: metricsRef.current.renderCount + 1,
        lastRenderTime: Date.now()
      }

      // Add memory usage if tracking is enabled
      if (trackMemory && 'memory' in performance) {
        const memoryInfo = (performance as any).memory
        ;(metricsRef.current as any).memoryUsage = memoryInfo.usedJSHeapSize
      }

      // Log slow renders
      if (renderTime > logThreshold) {
        logger.warn(`🚨 Slow render in ${componentName}: ${renderTime.toFixed(2)}ms`)
      }

      // Log excessive re-renders
      if (metricsRef.current.renderCount > 50) {
        logger.warn(`⚠️ High render count in ${componentName}: ${metricsRef.current.renderCount} renders`)
      }
    }
  })

  // Performance observer for additional metrics
  useEffect(() => {
    if (!trackMemory) return

    let observer: PerformanceObserver | null = null

    try {
      observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          if (entry.entryType === 'measure') {
            logger.log(`📊 ${componentName} - ${entry.name}: ${entry.duration.toFixed(2)}ms`)
          }
        })
      })

      observer.observe({ entryTypes: ['measure'] })
    } catch (error) {
      logger.warn('Performance observer not supported:', error)
    }

    return () => {
      observer?.disconnect()
    }
  }, [componentName, trackMemory])

  return metrics
}

// Note: Higher-order component removed to avoid JSX in .ts file
// Use the usePerformanceMonitor hook directly in components instead

// Bundle size monitoring utility
export function logBundleSize() {
  if (typeof window !== 'undefined' && 'performance' in window) {
    // Log navigation timing for bundle size insights
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming

    if (navigation) {
      const transferSize = navigation.transferSize
      const decodedBodySize = navigation.decodedBodySize

      logger.log(`📦 Bundle Size Analysis:
        - Transfer size: ${(transferSize / 1024 / 1024).toFixed(2)} MB
        - Decoded size: ${(decodedBodySize / 1024 / 1024).toFixed(2)} MB
        - Compression ratio: ${((1 - transferSize / decodedBodySize) * 100).toFixed(1)}%`)

      // Warn about large bundles
      if (decodedBodySize > 5 * 1024 * 1024) { // 5MB
        logger.warn('⚠️ Large bundle detected! Consider code splitting.')
      }
    }
  }
}

// Memory usage monitoring
export function getMemoryUsage() {
  if (typeof window !== 'undefined' && 'memory' in performance) {
    const memory = (performance as any).memory
    return {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024),
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024),
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024)
    }
  }
  return null
}

// FPS monitoring
export function useFPSMonitor() {
  const [fps, setFps] = useState(60)
  const frameCount = useRef(0)
  const lastTime = useRef(performance.now())

  useEffect(() => {
    let animationId: number

    const measureFPS = () => {
      const currentTime = performance.now()
      frameCount.current++

      if (currentTime - lastTime.current >= 1000) {
        setFps(Math.round((frameCount.current * 1000) / (currentTime - lastTime.current)))
        frameCount.current = 0
        lastTime.current = currentTime
      }

      animationId = requestAnimationFrame(measureFPS)
    }

    animationId = requestAnimationFrame(measureFPS)

    return () => {
      cancelAnimationFrame(animationId)
    }
  }, [])

  return fps
}